import moment from 'moment';
import type { DeadlineRow, DeadlineDisplayType } from './types';
import { ACTIVITY_TYPES } from './activityMeta';
import { loadStored, mergeStored } from '../../util/userScopedStorage';

const FILTERS_KEY = 'todo_deadlines_filters';
const PROJECTS_KEY = 'todo_deadlines_projects';
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export type StoredFilters = { types: DeadlineDisplayType[]; dates: string[] };
export type ProjectOption = { value: string; label: string };

export const toIsoDay = (deadline: string): string => moment(deadline).format('YYYY-MM-DD');

// Sets don't survive JSON.stringify, so the active filters persist as arrays. Both lists are
// re-validated on read: an unknown activity type or a malformed date would otherwise filter the
// table down to nothing behind a chip the user can't interpret.
export const loadFilters = (): StoredFilters => {
  const raw = loadStored(FILTERS_KEY);
  const types = Array.isArray(raw.types)
    ? raw.types.filter((v): v is DeadlineDisplayType => ACTIVITY_TYPES.includes(v as DeadlineDisplayType))
    : [];
  const dates = Array.isArray(raw.dates)
    ? raw.dates.filter((v): v is string => typeof v === 'string' && ISO_DAY.test(v))
    : [];
  return { types, dates };
};

// Merges rather than replaces: loadFilters only reads `types` and `dates`, so a wholesale write
// would delete any field a newer version of the app had added.
export const saveFilters = (types: Set<DeadlineDisplayType>, dates: Set<string>): void =>
  mergeStored(FILTERS_KEY, { types: Array.from(types), dates: Array.from(dates) });

// A selected day survives as long as ANY row still falls on it — deliberately ignoring the active
// type filter. Dropping a date is permanent (the caller persists the result), so it must only
// happen when the deadline is genuinely gone: dismissed, or aged out of the data. A type filter is
// a reversible lens; pruning against it would delete date selections the user can never get back
// by widening the filter again.
// Returns the original Set when nothing is dropped, so callers can skip a re-render.
export const pruneDates = (prev: Set<string>, rows: DeadlineRow[]): Set<string> => {
  const allowedDates = new Set(rows.map(r => toIsoDay(r.deadline)));
  const kept = new Set(Array.from(prev).filter(d => allowedDates.has(d)));
  return kept.size === prev.size ? prev : kept;
};

const isProjectOption = (v: unknown): v is ProjectOption =>
  typeof v === 'object' && v !== null
  && typeof (v as ProjectOption).value === 'string' && typeof (v as ProjectOption).label === 'string';

// Keyed per organization: project ids mean nothing in another org, where a chip would empty the list.
const projectsKey = (org: string): string => `${PROJECTS_KEY}_${org}`;

export const loadProjectFilters = (org: string): ProjectOption[] => {
  const raw = loadStored(projectsKey(org));
  return Array.isArray(raw.projects) ? raw.projects.filter(isProjectOption) : [];
};

export const saveProjectFilters = (org: string, projects: ProjectOption[]): void =>
  mergeStored(projectsKey(org), { projects });

export const projectOptionsOf = (rows: DeadlineRow[]): ProjectOption[] => {
  const projects = new Map<string, { name: string; number: string | null }>();
  for (const r of rows) {
    if (r.projectId && r.projectName) projects.set(r.projectId, { name: r.projectName, number: r.projectNumber });
  }
  const nameCounts = new Map<string, number>();
  projects.forEach(({ name }) => nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1));
  return Array.from(projects, ([value, { name, number }]) => ({
    value,
    label: number && (nameCounts.get(name) ?? 0) > 1 ? `${name} (${number})` : name,
  })).sort((a, b) => a.label.localeCompare(b.label, 'sv'));
};

export const filterByProjects = (rows: DeadlineRow[], projects: Set<string>): DeadlineRow[] =>
  projects.size === 0 ? rows : rows.filter(r => r.projectId !== null && projects.has(r.projectId));

export const filterBySearch = (rows: DeadlineRow[], query: string): DeadlineRow[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(r =>
    [r.description, r.projectName, r.projectNumber].some(text => text?.toLowerCase().includes(needle)));
};
